import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { ApiError, getMe } from "@/features/identity/identity.api";
import type {
  MeResponse,
  PublicAccount,
  PublicAlumniMembership,
  PublicMembership,
  PublicUser,
  PublicWorkspace,
} from "@/features/identity/schemas/auth.schema";

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: PublicUser | null;
  account: PublicAccount | null;
  workspaces: ReadonlyArray<PublicWorkspace>;
  memberships: ReadonlyArray<PublicMembership>;
  /// PAYD-FR-042 (H-06). Workspaces the caller has LEFT and can still reach
  /// their own pay records in. Never a source of workspace chrome: these
  /// carry no name, no slug and no role, and an alumni session is refused on
  /// every route the shore has not tagged `@AlumniAllowed()`.
  alumniMemberships: ReadonlyArray<PublicAlumniMembership>;
  refresh: () => Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Wraps the app with auth state pulled from `/auth/me`.
 *
 * - 401 → user is anonymous (`isAuthenticated: false`).
 * - 200 → fully resolved user + account + workspaces.
 * - any other failure → falls back to anonymous (logged, but UI continues).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useQuery<MeResponse>({
    queryKey: ["auth", "me"],
    queryFn: getMe,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 1;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const value = useMemo<AuthContextValue>(() => {
    const me = query.data ?? null;
    return {
      isAuthenticated: me !== null,
      isLoading: query.isLoading,
      user: me?.user ?? null,
      account: me?.account ?? null,
      workspaces: me?.workspaces ?? [],
      memberships: me?.memberships ?? [],
      alumniMemberships: me?.alumniMemberships ?? [],
      refresh: async () => {
        const result = await query.refetch();
        return result.data ?? null;
      },
    };
  }, [query]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
